import React, { useState } from 'react'
import axios, { AxiosError } from 'axios'

import ModalDialogComponent from '../../shared/components/modal-dialog/modal-dialog'
import { HTTPStatusCode } from '../../shared/constants/http-status-codes'

declare module 'axios' {
    interface AxiosRequestConfig {
        // statuses the caller handles itself, so the global error modal stays closed for them
        skipErrorModalForStatuses?: number[]
    }
}

// Functionality is written as React component to be able to use error-boundary hook
const AxiosErrorHandler = (): React.ReactElement<any> => {
    const [errors, setErrors] = useState({})

    axios.interceptors.response.use(
        function (response) {
            // Do something with response data
            return response
        },
        function (error: AxiosError) {
            // when the error is canceled by axios we receive only a message but we send an object so we parse it
            const preparedError = axios.isCancel(error) ? JSON.parse(error.message) : error

            if (preparedError?.customStatus === HTTPStatusCode.CANCELED) {
                // do not handle canceled request by a user
            } else if (preparedError?.config?.skipErrorModalForStatuses?.includes(preparedError.response?.status)) {
                // the caller handles this status itself
            } else if (preparedError?.customStatus) {
                setErrors({
                    customErrorMessage: {
                        response: {
                            status: preparedError.customStatus,
                            data: {
                                errors: [{ message: preparedError.message }],
                            },
                        },
                    },
                })
            } else if (!preparedError.response?.status) {
                setErrors({
                    customErrorMessage: {
                        response: {
                            status: 504,
                            data: {
                                errors: [
                                    {
                                        message:
                                            'Failed to load data: ' + error.config?.method + ' ' + error.config?.url,
                                    },
                                ],
                            },
                        },
                    },
                })
            } else {
                setErrors({
                    ...errors,
                    [Object.values(errors).length]: {
                        ...preparedError,
                        response: {
                            ...preparedError.response,
                            data: {
                                errors: preparedError.response.data.errors,
                            },
                        },
                    },
                })
            }

            return Promise.reject(preparedError)
        }
    )

    const totalErrors = Object.values(errors).length
    const modalBody = (): string | React.ReactElement<any> => {
        if (totalErrors) {
            return (
                <>
                    {Object.values(errors).map((error: any, index) => {
                        const code = error?.response?.status
                        // Display run-time or back-end massage
                        const msg = code
                            ? error.response?.data?.errors?.map((err, i: number) => <div key={i}>{err.message}</div>)
                            : error.message

                        return (
                            <div key={index}>
                                <div>{code ? <b>Code: {code}</b> : ''}</div>
                                <br></br>
                                <div>{msg ? <b>Message: {msg}</b> : ''}</div>
                                <br></br>
                            </div>
                        )
                    })}
                </>
            )
        }

        return 'No errors found'
    }

    return totalErrors ? (
        <ModalDialogComponent body={modalBody()} closeButton handleClose={(): void => setErrors({})} title="Error" />
    ) : (
        <></>
    )
}

export default AxiosErrorHandler
